import { useEffect, useMemo, useState } from 'react';
import { animate } from 'framer-motion';
import { Timer, Trophy, Users, Flame } from 'lucide-react';
import { useI18n } from '../../../i18n';

/* Device / capability detection shared by the login experience */
export const isMobileDevice = typeof window !== 'undefined' &&
  (window.matchMedia('(max-width: 1023px)').matches || navigator.maxTouchPoints > 0);

export const prefersReducedMotion = typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function supportsWebGL() {
  try {
    const canvas = document.createElement('canvas');
    return !!(canvas.getContext('webgl2') || canvas.getContext('webgl'));
  } catch {
    return false;
  }
}

/*
 * Whether the animated 3D backdrop is worth starting on this device:
 * cheap hardware signals before three.js loads, plus a remembered verdict
 * from the in-scene frame monitor so a stuttering device skips it next time.
 */
const PERF_KEY = 'mykart_login_perf';
const PERF_TTL = 30 * 24 * 60 * 60 * 1000;
const SOFTWARE_GPU = /swiftshader|llvmpipe|softpipe|basic render|software/i;

let gpuName;
export function getGpuName() {
  if (gpuName !== undefined) return gpuName;
  gpuName = '';
  try {
    const gl = document.createElement('canvas').getContext('webgl');
    const info = gl?.getExtension('WEBGL_debug_renderer_info');
    gpuName = String((info ? gl.getParameter(info.UNMASKED_RENDERER_WEBGL) : gl?.getParameter(gl.RENDERER)) || '');
    gl?.getExtension('WEBGL_lose_context')?.loseContext();
  } catch { /* keep the empty name */ }
  return gpuName;
}

export function isWeakDevice() {
  const connection = navigator.connection;
  return SOFTWARE_GPU.test(getGpuName()) ||
    navigator.deviceMemory < 4 ||
    navigator.hardwareConcurrency <= 2 ||
    !!connection?.saveData ||
    /(^|-)2g$/.test(connection?.effectiveType || '');
}

export function hasSlowVerdict() {
  try {
    const verdict = JSON.parse(localStorage.getItem(PERF_KEY));
    return !!verdict && verdict.gpu === getGpuName() && Date.now() - verdict.at < PERF_TTL;
  } catch {
    return false;
  }
}

export function saveSlowVerdict() {
  try { localStorage.setItem(PERF_KEY, JSON.stringify({ gpu: getGpuName(), at: Date.now() })); } catch { /* storage blocked */ }
}

export function clearSlowVerdict() {
  try { localStorage.removeItem(PERF_KEY); } catch { /* storage blocked */ }
}

/*
 * Mutable channel between the DOM (form focus/typing, page scroll) and
 * the three.js scene. The scene reads it every frame inside useFrame,
 * so writes here never trigger React re-renders.
 *  - scroll: 0..1 progress through the landing page
 *  - focus:  currently focused form field or null
 *  - kick:   impulse (typing = 1, submit = 3) that decays in the scene
 */
export const sceneState = {
  scroll: 0,
  focus: null,
  kick: 0,
  hudSpeed: 0, // written by the scene each frame, read by the DOM race HUD
  pulse: null, // 'error' | 'success' — one-shot login outcome, consumed by the scene
};

export function kickScene(strength = 1) {
  sceneState.kick = Math.max(sceneState.kick, strength);
}

/* Animated integer count-up (returns a plain formatted string node) */
export function CountUp({ value, duration = 1.4 }) {
  const { locale } = useI18n();
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const controls = animate(0, value, {
      duration,
      ease: 'easeOut',
      onUpdate: v => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [value, duration]);
  return display.toLocaleString(locale);
}

// Firestore Lite on demand keeps the full SDK out of the login entry chunk.
let liteDbPromise;
function loadCommunityStats() {
  if (!liteDbPromise) {
    liteDbPromise = Promise.all([import('firebase/firestore/lite'), import('../../../firebaseApp')])
      .then(([lite, { default: app }]) => {
        const db = process.env.REACT_APP_GAMES_STAGING === 'true'
          ? lite.getFirestore(app, 'games-staging') : lite.getFirestore(app);
        if (process.env.REACT_APP_USE_EMULATORS === 'true') lite.connectFirestoreEmulator(db, '127.0.0.1', 8080);
        return { db, ...lite };
      });
  }
  return liteDbPromise.then(({ db, doc, getDoc }) => getDoc(doc(db, 'stats', 'community')));
}

/* Live community counter (public stats doc, readable before login) */
export function useCommunityCount() {
  const [count, setCount] = useState(null);
  useEffect(() => {
    let cancelled = false;
    loadCommunityStats()
      .then(snap => {
        if (!cancelled && snap.exists()) setCount(snap.data().userCount ?? 0);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return count;
}

const FEATURE_ICONS = [Timer, Trophy, Users, Flame];
const FEATURE_COLORS = ['#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444'];

/* Feature list from i18n, decorated with icons + colors */
export function useAuthFeatures() {
  const { getRaw } = useI18n();
  return useMemo(() => (getRaw('auth.features') || []).map((item, index) => ({
    icon: FEATURE_ICONS[index],
    text: item.text,
    desc: item.desc,
    color: FEATURE_COLORS[index],
  })), [getRaw]);
}
